import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Auth } from './components/Auth';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { MapView } from './components/Map';
import { YouTubePlayer } from './components/YouTubePlayer';
import { CreatePinModal } from './components/CreatePinModal';
import { EditPinModal } from './components/EditPinModal';
import { LandingPage } from './components/LandingPage';
import { NearbySoundDrops } from './components/NearbySoundDrops';
import { AdminPanel } from './components/AdminPanel';
import { PlaylistManager } from './components/PlaylistManager';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { CreateZoneModal } from './components/CreateZoneModal';
import { CreateTrailModal } from './components/CreateTrailModal';
import { SoundZoneBadge } from './components/SoundZoneBadge';
import { ZoneBottomPanel } from './components/ZoneBottomPanel';
import { ZonePlayer } from './components/ZonePlayer';
import { EditZoneModal } from './components/EditZoneModal';
import { supabase, AudioPin } from './lib/supabase';
import { useGeolocation } from './hooks/useGeolocation';
import { useZoneDetection } from './hooks/useZoneDetection';
import { Loader, Radio, Route } from 'lucide-react';

function AppContent() {
  const { user, profile, loading: authLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [audioPins, setAudioPins] = useState<AudioPin[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [selectedPin, setSelectedPin] = useState<AudioPin | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLocation, setCreateLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [activeView, setActiveView] = useState<'map' | 'discover' | 'nearby' | 'profile' | 'admin' | 'playlists' | 'analytics'>('map');
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [showTrailModal, setShowTrailModal] = useState(false);
  const [isDrawingZone, setIsDrawingZone] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState<[number, number][] | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPin, setEditingPin] = useState<AudioPin | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-98.5795, 39.8283]);
  const [mapZoom, setMapZoom] = useState(4);
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [selectedZone, setSelectedZone] = useState<any | null>(null);
  const [showZoneBadge, setShowZoneBadge] = useState(false);
  const [isPlayerPlaying, setIsPlayerPlaying] = useState(false);
  const [editingZone, setEditingZone] = useState<any | null>(null);
  const userLocation = useGeolocation();
  const { currentZone, justEnteredZone } = useZoneDetection(zones, userLocation);

  useEffect(() => {
    if (user) {
      fetchAudioPins();
      fetchZones();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (justEnteredZone && currentZone) {
      setShowZoneBadge(true);
    }
  }, [justEnteredZone, currentZone]);

  const fetchAudioPins = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('audio_pins')
        .select(`
          *,
          profiles:user_id (
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setAudioPins(data || []);
    } catch (error) {
      console.error('Error fetching audio pins:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const { data, error } = await supabase
        .from('sound_zones')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setZones(data || []);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    if (isDrawingZone) {
      return;
    }
    setCreateLocation({ lat, lng });
    setShowCreateModal(true);
    setSearchLocation(null);
  };

  const handleCreatePinClick = () => {
    // Use existing user location if available
    if (userLocation) {
      setCreateLocation({
        lat: userLocation.latitude,
        lng: userLocation.longitude
      });
      setShowCreateModal(true);
      return;
    }

    // Otherwise try to get current position
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCreateLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setShowCreateModal(true);
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Fallback to map center if geolocation fails
          const [lng, lat] = mapCenter;
          setCreateLocation({ lat, lng });
          setShowCreateModal(true);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 60000
        }
      );
    } else {
      // Fallback to map center
      const [lng, lat] = mapCenter;
      setCreateLocation({ lat, lng });
      setShowCreateModal(true);
    }
  };

  const handlePinClick = async (pin: AudioPin) => {
    setSelectedZone(null);

    const pinsAtLocation = audioPins.filter(
      p => Math.abs(p.latitude - pin.latitude) < 0.0001 &&
           Math.abs(p.longitude - pin.longitude) < 0.0001
    );

    // Always create a new object to ensure React detects the change
    if (pinsAtLocation.length > 1) {
      setSelectedPin({ ...pin, nearbyPins: pinsAtLocation, _timestamp: Date.now() } as any);
    } else {
      setSelectedPin({ ...pin, _timestamp: Date.now() });
    }

    await supabase
      .from('audio_pins')
      .update({ play_count: pin.play_count + 1 })
      .eq('id', pin.id);

    setAudioPins(prev =>
      prev.map(p => (p.id === pin.id ? { ...p, play_count: p.play_count + 1 } : p))
    );
  };

  const handleCreateSuccess = async () => {
    await fetchAudioPins();

    // If there's a selected pin, refresh it with updated data
    if (selectedPin) {
      const { data, error } = await supabase
        .from('audio_pins')
        .select(`
          *,
          profiles:user_id (
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('id', selectedPin.id)
        .maybeSingle();

      if (data && !error) {
        const nearbyPins = (selectedPin as any).nearbyPins;
        if (nearbyPins) {
          setSelectedPin({ ...data, nearbyPins, _timestamp: Date.now() } as any);
        } else {
          setSelectedPin({ ...data, _timestamp: Date.now() });
        }
      }
    }
  };

  const handleDeletePin = async (pin: AudioPin) => {
    try {
      const { error } = await supabase
        .from('audio_pins')
        .delete()
        .eq('id', pin.id);

      if (error) throw error;

      setAudioPins(prev => prev.filter(p => p.id !== pin.id));

      if (selectedPin?.id === pin.id) {
        setSelectedPin(null);
      }
    } catch (error) {
      console.error('Error deleting pin:', error);
      alert('Failed to delete pin');
    }
  };

  const handleDeleteZone = async (zone: any) => {
    if (!confirm('Are you sure you want to delete this sound zone?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sound_zones')
        .delete()
        .eq('id', zone.id);

      if (error) throw error;

      setZones(prev => prev.filter(z => z.id !== zone.id));

      if (selectedZone?.id === zone.id) {
        setSelectedZone(null);
      }
    } catch (error) {
      console.error('Error deleting zone:', error);
      alert('Failed to delete zone');
    }
  };

  const handleLocationSearch = (lat: number, lng: number, name: string) => {
    setMapCenter([lng, lat]);
    setMapZoom(12);
    setSearchLocation({ lat, lng, name });
    setActiveView('map');
  };

  const filteredPins = searchQuery
    ? (audioPins || []).filter(pin =>
        pin.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pin.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pin.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : (audioPins || []);

  if (authLoading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <Loader className="w-12 h-12 text-[#00c7b7] animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    if (showAuth) {
      return (
        <>
          <Auth onBack={() => setShowAuth(false)} />
        </>
      );
    }
    return (
      <>
        <LandingPage onGetStarted={() => setShowAuth(true)} />
      </>
    );
  }

  return (
    <>
      <div className="h-screen flex flex-col bg-gray-900">
        <Header onSearch={setSearchQuery} onLocationSearch={handleLocationSearch} />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          activeView={activeView}
          onViewChange={setActiveView}
          isAdmin={profile?.role === 'admin'}
        />

        <main className="flex-1 relative">
          {activeView === 'map' && !isDrawingZone && (
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={() => setShowZoneModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] shadow-lg font-medium transition-all"
              >
                <Radio className="w-4 h-4" />
                Create Zone
              </button>
              <button
                onClick={() => setShowTrailModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-lg font-medium"
              >
                <Route className="w-4 h-4" />
                Create Trail
              </button>
            </div>
          )}
          {activeView === 'map' && isDrawingZone && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
              <div className="bg-[#00c7b7] text-white px-6 py-3 rounded-lg shadow-lg font-medium flex items-center gap-2">
                <Radio className="w-5 h-5 animate-pulse" />
                <span>Click on map to draw Sound Zone polygon. Click near first point to close.</span>
              </div>
            </div>
          )}
          {activeView === 'map' && (
            <>
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Loader className="w-12 h-12 text-[#00c7b7] animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Loading audio pins...</p>
                  </div>
                </div>
              ) : (
                <>
                  <MapView
                    audioPins={filteredPins}
                    zones={zones}
                    onPinClick={handlePinClick}
                    onMapClick={handleMapClick}
                    onZoneClick={(zone: any) => {
                      setSelectedPin(null);
                      setSelectedZone(zone);
                    }}
                    onEditPin={setEditingPin}
                    onDeletePin={handleDeletePin}
                    center={mapCenter}
                    zoom={mapZoom}
                    searchQuery={searchQuery}
                    currentUserId={user?.id}
                    onMapMove={(center, zoom) => {
                      setMapCenter(center);
                      setMapZoom(zoom);
                    }}
                    searchLocation={searchLocation}
                    isDrawingZone={isDrawingZone}
                    drawnPolygon={drawnPolygon}
                    onPolygonUpdate={setDrawnPolygon}
                  />
                </>
              )}
            </>
          )}

          {activeView === 'nearby' && (
            <NearbySoundDrops />
          )}

          {activeView === 'admin' && profile?.role === 'admin' && (
            <AdminPanel />
          )}

          {activeView === 'playlists' && (
            <div className="h-full p-8 overflow-y-auto">
              <PlaylistManager />
            </div>
          )}

          {activeView === 'analytics' && (
            <div className="h-full p-8 overflow-y-auto">
              <AnalyticsDashboard />
            </div>
          )}

          {activeView === 'discover' && (
            <div className={`h-full p-8 overflow-y-auto ${selectedPin ? 'pb-[500px]' : ''}`}>
              <h2 className="text-2xl font-bold mb-6 text-white">Discover</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPins.map((pin) => (
                  <div
                    key={pin.id}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
                  >
                    <div
                      onClick={() => handlePinClick(pin)}
                      className="cursor-pointer"
                    >
                      <div className="aspect-video bg-gray-200 rounded-lg mb-3 relative overflow-hidden">
                        <img
                          src={`https://img.youtube.com/vi/${pin.youtube_video_id}/mqdefault.jpg`}
                          alt={pin.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-lg mb-1 truncate text-white">{pin.title}</h3>
                      <p className="text-sm text-gray-400 mb-2 line-clamp-2">{pin.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{pin.play_count} plays</span>
                        <span>{pin.like_count} likes</span>
                      </div>
                    </div>
                    {user?.id === pin.user_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPin(pin);
                        }}
                        className="mt-3 w-full px-4 py-2 bg-[#00c7b7] hover:bg-[#14f0d5] text-white text-sm rounded-lg font-semibold transition-colors"
                      >
                        Edit Pin
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === 'profile' && (
            <div className={`h-full p-8 overflow-y-auto ${selectedPin ? 'pb-[500px]' : ''}`}>
              <h2 className="text-2xl font-bold mb-6 text-white">Your Profile</h2>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-2 text-white">Account Info</h3>
                <p className="text-sm text-gray-400">Username: {profile?.username}</p>
                <p className="text-sm text-gray-400">Display Name: {profile?.display_name || 'Not set'}</p>
                <p className="text-sm text-gray-400 mt-2">User ID: {user?.id}</p>
              </div>

              <h3 className="text-xl font-bold mb-4 text-white">Your Audio Pins</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {audioPins.filter(pin => pin.user_id === user?.id).map((pin) => (
                  <div
                    key={pin.id}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
                  >
                    <div
                      onClick={() => handlePinClick(pin)}
                      className="cursor-pointer"
                    >
                      <div className="aspect-video bg-gray-200 rounded-lg mb-3 relative overflow-hidden">
                        <img
                          src={`https://img.youtube.com/vi/${pin.youtube_video_id}/mqdefault.jpg`}
                          alt={pin.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-lg mb-1 truncate text-white">{pin.title}</h3>
                      <p className="text-sm text-gray-400 mb-2 line-clamp-2">{pin.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{pin.play_count} plays</span>
                        <span>{pin.is_public ? 'Public' : 'Private'}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPin(pin);
                      }}
                      className="mt-3 w-full px-4 py-2 bg-[#00c7b7] hover:bg-[#14f0d5] text-white text-sm rounded-lg font-semibold transition-colors"
                    >
                      Edit Pin
                    </button>
                  </div>
                ))}
              </div>
              {audioPins.filter(pin => pin.user_id === user?.id).length === 0 && (
                <div className="text-center py-12 bg-gray-800 border border-gray-700 rounded-lg">
                  <p className="text-gray-300">You haven't created any audio pins yet.</p>
                  <p className="text-gray-500 text-sm mt-2">Click on the map to create your first pin!</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {selectedPin && (
        <YouTubePlayer
          pin={selectedPin}
          pins={(selectedPin as any).nearbyPins || [selectedPin]}
          onClose={() => setSelectedPin(null)}
          onEdit={() => setEditingPin(selectedPin)}
          onPinSelect={(pin) => {
            const nearbyPins = (selectedPin as any).nearbyPins;
            if (nearbyPins) {
              setSelectedPin({ ...pin, nearbyPins } as any);
            } else {
              setSelectedPin(pin);
            }
          }}
          canEdit={selectedPin.user_id === user?.id}
        />
      )}

      {showCreateModal && createLocation && (
        <CreatePinModal
          latitude={createLocation.lat}
          longitude={createLocation.lng}
          onClose={() => {
            setShowCreateModal(false);
            setCreateLocation(null);
          }}
          onSuccess={handleCreateSuccess}
        />
      )}

      {editingPin && (
        <EditPinModal
          pin={editingPin}
          onClose={() => setEditingPin(null)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {selectedZone && (
        <ZonePlayer
          zone={selectedZone}
          zones={selectedZone._groupZones || [selectedZone]}
          onClose={() => setSelectedZone(null)}
          onEdit={() => setEditingZone(selectedZone)}
          onZoneSelect={(zone) => setSelectedZone({ ...zone, _groupZones: selectedZone._groupZones })}
          canEdit={user?.id === selectedZone.user_id}
          onAddSoundDrop={(lat, lng) => {
            setCreateLocation({ lat, lng });
            setShowCreateModal(true);
          }}
        />
      )}

      {editingZone && (
        <EditZoneModal
          zone={editingZone}
          onClose={() => setEditingZone(null)}
          onSuccess={() => {
            fetchZones();
            setEditingZone(null);
            setSelectedZone(null);
          }}
        />
      )}

      {showZoneModal && (
        <CreateZoneModal
          isOpen={showZoneModal}
          onClose={() => {
            setShowZoneModal(false);
            setIsDrawingZone(false);
            setDrawnPolygon(null);
          }}
          onZoneCreated={() => {
            setShowZoneModal(false);
            setIsDrawingZone(false);
            setDrawnPolygon(null);
            fetchAudioPins();
            fetchZones();
          }}
          onStartDrawing={() => {
            setIsDrawingZone(true);
          }}
          drawnPolygon={drawnPolygon}
        />
      )}

      {showTrailModal && (
        <CreateTrailModal
          isOpen={showTrailModal}
          onClose={() => setShowTrailModal(false)}
          onTrailCreated={() => {
            setShowTrailModal(false);
          }}
        />
      )}

      {showZoneBadge && currentZone && (
        <SoundZoneBadge
          zoneName={currentZone.name}
          onDismiss={() => setShowZoneBadge(false)}
        />
      )}

      {currentZone && (
        <ZoneBottomPanel
          zone={currentZone}
          onClose={() => {}}
          onPlayTrack={(url) => {
            setSelectedPin({
              id: '',
              user_id: '',
              title: 'Zone Track',
              description: '',
              youtube_url: url,
              youtube_video_id: url.split('v=')[1]?.split('&')[0] || '',
              latitude: currentZone.latitude,
              longitude: currentZone.longitude,
              location_name: currentZone.name,
              is_public: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          }}
          isPlaying={isPlayerPlaying}
          onTogglePlay={() => setIsPlayerPlaying(!isPlayerPlaying)}
          currentTrackUrl={selectedPin?.youtube_url}
          onAddSoundDrop={(lat, lng) => {
            setCreateLocation({ lat, lng });
            setShowCreateModal(true);
          }}
        />
      )}
      </div>
    </>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}
